import { Box, Grid } from '@mui/material';
import { TournamentStatus } from '@okletsplay/core';
import { DialogName } from 'app/enums';
import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { TournamentEntity } from 'app/interfaces/entity';
import { modalsReducers } from 'app/store/modal/slice';
import classNames from 'classnames';
import { CurrentTournamentStoreStatus } from 'modules/tournaments/store/current-tournament/enum';
import { cancelTournament } from 'modules/tournaments/store/current-tournament/repository';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmationDialog } from 'shared/components/ConfirmationDialog';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { TournamentSettingsTitle } from '../../TournamentSettingsTitle';
import { useStyles } from './TournamentActions.styles';

interface TournamentActionsProps {
  tournament: TournamentEntity;
}

export const TournamentActions: FC<TournamentActionsProps> = ({ tournament }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const isCurrentUserAdmin = useSelector(currentTournamentSelector.isCurrentUserAdmin);
  const canceling = useEntityRepositoryStatus({
    [EntityStoreName.CurrentTournament]: CurrentTournamentStoreStatus.Canceling,
  });

  const cancelTournamentHandler = useCallback(
    () => dispatch(cancelTournament(tournament.id)),
    [dispatch, tournament.id]
  );

  const openConfirmDialog = useCallback(
    () => dispatch(modalsReducers.openModal({ name: DialogName.Confirmation })),
    [dispatch]
  );

  if (
    !isCurrentUserAdmin ||
    !tournament ||
    [TournamentStatus.Finished, TournamentStatus.Canceled].includes(tournament.status)
  ) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <TournamentSettingsTitle>Tournament Actions</TournamentSettingsTitle>
      <Grid container spacing={2} className={classes.root}>
        <Grid xs={12} sm={6} md={4} lg={6} xl={6} item>
          <LoaderButton
            size="medium"
            color="error"
            variant="outlined"
            onClick={openConfirmDialog}
            loading={canceling}
            className={classNames(classes.button, classes.cancel)}
            disabled={canceling}
          >
            Cancel Tournament
          </LoaderButton>
        </Grid>
      </Grid>
      <ConfirmationDialog
        title={`Cancel tournament ${tournament.name}`}
        description="Are you sure you want to cancel the tournament?"
        onAccept={cancelTournamentHandler}
      />
    </Box>
  );
};
